import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { Music, Trash2, Upload, Loader2 } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { InvitationFormValues } from '@/types/invitation-schema';

interface Props {
  invitationId: string | undefined;
}

const MAX_AUDIO_SIZE = 2 * 1024 * 1024;
const ALLOWED_AUDIO_TYPES = ['audio/mp3', 'audio/mpeg'];
const VALID_AUDIO_EXTENSIONS = ['mp3', 'mpeg'];
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24 * 365;

const MusicTab = ({ invitationId }: Props) => {
  const form = useFormContext<InvitationFormValues>();
  const { user } = useAuth();
  const enabled = form.watch('background_music_enabled');
  const currentUrl = form.watch('background_music_url');
  const [uploading, setUploading] = useState(false);

  const isNew = !invitationId;

  const handleAudioUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !invitationId || !user) return;

    if (!ALLOWED_AUDIO_TYPES.includes(file.type)) {
      toast.error('Solo se permiten archivos MP3');
      e.target.value = '';
      return;
    }

    if (file.size > MAX_AUDIO_SIZE) {
      toast.error('El archivo no debe superar 2 MB');
      e.target.value = '';
      return;
    }

    const fileExt = file.name.split('.').pop()?.toLowerCase();
    if (!fileExt || !VALID_AUDIO_EXTENSIONS.includes(fileExt)) {
      toast.error('Extensión de archivo no válida');
      e.target.value = '';
      return;
    }

    setUploading(true);
    const filePath = `${user.id}/${invitationId}/${Date.now()}.mp3`;

    const { error: uploadError } = await supabase.storage
      .from('invitation-audio')
      .upload(filePath, file, { contentType: 'audio/mpeg', upsert: true });

    if (uploadError) {
      setUploading(false);
      toast.error('Error al subir el audio: ' + uploadError.message);
      e.target.value = '';
      return;
    }

    const { data: signedData, error: signedError } = await supabase.storage
      .from('invitation-audio')
      .createSignedUrl(filePath, SIGNED_URL_TTL_SECONDS);

    setUploading(false);

    if (signedError || !signedData?.signedUrl) {
      toast.error('No se pudo generar la URL firmada');
      e.target.value = '';
      return;
    }

    form.setValue('background_music_url', signedData.signedUrl, {
      shouldDirty: true,
    });
    toast.success('Audio cargado');
    e.target.value = '';
  };

  const handleClearAudio = () => {
    form.setValue('background_music_url', '', { shouldDirty: true });
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Música de Fondo</CardTitle>
        <CardDescription>
          Sube un MP3 para reproducir al abrir la invitación
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <FormField
          control={form.control}
          name="background_music_enabled"
          render={({ field }) => (
            <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
              <div>
                <p className="font-medium">Habilitar música</p>
                <p className="text-sm text-muted-foreground">
                  Reproducir música al ver la invitación
                </p>
              </div>
              <Switch checked={field.value} onCheckedChange={field.onChange} />
            </div>
          )}
        />

        {enabled && (
          <>
            {!isNew && (
              <div className="border-2 border-dashed rounded-lg p-6 text-center">
                <input
                  type="file"
                  accept="audio/mp3,audio/mpeg"
                  onChange={handleAudioUpload}
                  className="hidden"
                  id="audio-upload"
                  disabled={uploading}
                />
                <label
                  htmlFor="audio-upload"
                  className="cursor-pointer inline-flex flex-col items-center"
                >
                  {uploading ? (
                    <Loader2 className="w-10 h-10 mx-auto text-muted-foreground mb-3 animate-spin" />
                  ) : (
                    <Music className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
                  )}
                  <p className="font-medium">
                    {uploading ? 'Subiendo…' : 'Haz clic para subir MP3'}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    MP3 hasta 2 MB
                  </p>
                </label>
              </div>
            )}

            {currentUrl && (
              <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
                <Music className="w-4 h-4 text-muted-foreground shrink-0" />
                <audio
                  controls
                  src={currentUrl}
                  className="flex-1 max-w-full"
                  preload="none"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  type="button"
                  onClick={handleClearAudio}
                  aria-label="Quitar audio"
                >
                  <Trash2 className="w-4 h-4 text-destructive" />
                </Button>
              </div>
            )}

            <FormField
              control={form.control}
              name="background_music_url"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>O pega una URL de MP3 / streaming</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="https://ejemplo.com/musica.mp3"
                      {...field}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            {isNew && (
              <p className="text-sm text-muted-foreground">
                <Upload className="inline w-3.5 h-3.5 mr-1" />
                Guarda la invitación primero para subir un MP3.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default MusicTab;
